/**
 * GamesScreen — каталог игр.
 *
 * Одна задача: найти игру. Витрина с подборками живёт на главной.
 *
 * Что изменилось при переводе на дизайн-систему:
 *  • Фильтры были тремя рядами чипов — до 20 кнопок одновременно, они
 *    занимали пол-экрана над результатами. Теперь сортировка это вкладки
 *    (это разделы каталога), а возраст/жанр/мультиплеер убраны в панель,
 *    которая раскрывается по кнопке со счётчиком активных фильтров.
 *  • Догрузка показывала текст «Загружаем…» — теперь скелетоны в форме
 *    карточек: раскладка не прыгает, и видно, сколько ещё придёт.
 *  • Ошибка была голой строкой без выхода. Теперь состояние с кнопкой
 *    «Повторить»: тупик без действия — не состояние, а сообщение о сбое.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as cabinetApi from "../../../api/cabinet";
import GameCard, { GameCardSkeleton } from "../../game/GameCard";
import CabIcon from "../CabIcon";
import { Button, Input, Pill, Tabs, EmptyState, fmtNum } from "../../ui";
import { genreOptions, AGE_RATINGS } from "../../../lib/gameMeta";
import "../../../styles/cabinet-games.css";

const PER_PAGE = 24;

/* Сортировка — это РАЗДЕЛЫ каталога (разное содержимое), поэтому вкладки,
   а не сегмент-контрол. Правило из дизайн-системы, раздел 10. */
const SORTS = [
  { id: "recommended", label: "Рекомендуем" },
  { id: "popular", label: "Популярные" },
  { id: "new", label: "Новые" },
  { id: "top_week", label: "Топ недели" },
];

/* Фильтры переживают перезагрузку: человек настроил выборку, ушёл в игру
   и вернулся — терять её было бы обидно. */
const LS = {
  sort: "rb_cat_sort",
  age: "rb_cat_age",
  genre: "rb_cat_genre",
  mp: "rb_cat_mp",
};

function load(key, def) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? def : v;
  } catch {
    return def;
  }
}
function save(key, v) {
  try {
    localStorage.setItem(key, String(v));
  } catch {
    /* приватный режим */
  }
}

export default function GamesScreen() {
  const navigate = useNavigate();

  const [sort, setSort] = useState(() => load(LS.sort, "recommended"));
  const [age, setAge] = useState(() => Number(load(LS.age, 18)));
  const [genre, setGenre] = useState(() => load(LS.genre, ""));
  const [mpOnly, setMpOnly] = useState(() => load(LS.mp, "0") === "1");

  const [query, setQuery] = useState("");
  const [games, setGames] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const abortRef = useRef(null);
  const sentinelRef = useRef(null);

  /* Загрузка страницы. append=false — новая выборка, true — догрузка. */
  const fetchPage = useCallback(
    async (pageNum, append) => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setLoading(true);
      setError(null);
      try {
        const res = await cabinetApi.getFeedPage(
          {
            page: pageNum,
            perPage: PER_PAGE,
            tab: sort,
            maxAge: age,
            genre: genre || undefined,
          },
          ctrl.signal
        );
        const list = Array.isArray(res?.projects) ? res.projects : [];

        setTotal(typeof res?.total === "number" ? res.total : null);
        setGames((prev) => {
          const next = append ? [...prev, ...list] : list;
          // Дедупликация: при догрузке бэкенд иногда отдаёт пересечение
          // страниц, и React ругается на одинаковые ключи.
          const seen = new Set();
          return next.filter((g) => {
            if (seen.has(g.id)) return false;
            seen.add(g.id);
            return true;
          });
        });
        setHasMore(list.length >= PER_PAGE);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Не удалось загрузить игры");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    },
    [sort, age, genre]
  );

  /* Смена фильтров — всегда с первой страницы. */
  useEffect(() => {
    setPage(1);
    fetchPage(1, false);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchPage]);

  /* Поиск — отдельный путь: у бэкенда свой эндпоинт без пагинации.
     Дебаунс только при непустом запросе; сброс поиска должен вернуть
     каталог мгновенно, а не через треть секунды. */
  useEffect(() => {
    const q = query.trim();
    if (!q) return undefined;

    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);
      try {
        const res = await cabinetApi.searchProjects(q, age, ctrl.signal);
        const list = Array.isArray(res) ? res : res?.projects || [];
        setGames(list);
        setTotal(list.length);
        setHasMore(false);
      } catch (e) {
        if (e?.name !== "AbortError") setError("Поиск не сработал");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, age]);

  /* Сброс поиска возвращает каталог: без этого экран остаётся с
     результатами поиска, хотя поле уже пустое. */
  const clearQuery = () => {
    setQuery("");
    setPage(1);
    fetchPage(1, false);
  };

  /* Бесконечная прокрутка. rootMargin — грузим заранее, чтобы человек
     не упирался в конец списка и не ждал. */
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || query.trim()) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const next = page + 1;
          setPage(next);
          fetchPage(next, true);
        }
      },
      { rootMargin: "600px" }
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore, loading, page, fetchPage, query]);

  const setAndSave = (setter, key) => (value) => {
    setter(value);
    save(key, value);
  };

  const onSort = setAndSave(setSort, LS.sort);
  const onAge = setAndSave(setAge, LS.age);
  const onGenre = setAndSave(setGenre, LS.genre);
  const onMp = (v) => {
    setMpOnly(v);
    save(LS.mp, v ? "1" : "0");
  };

  const resetAll = () => {
    onAge(18);
    onGenre("");
    onMp(false);
    setQuery("");
  };

  // Мультиплеер фильтруем НА КЛИЕНТЕ: проверено — бэкенд параметр
  // `multiplayer` игнорирует и отдаёт весь каталог.
  const visible = mpOnly ? games.filter((g) => g.multiplayer) : games;

  /* Счётчик активных фильтров на кнопке: свёрнутая панель иначе скрывает
     тот факт, что выборка сужена, и результат выглядит неполным без
     видимой причины. Сортировка сюда не входит — она видна во вкладках. */
  const activeCount = useMemo(
    () => (age !== 18 ? 1 : 0) + (genre ? 1 : 0) + (mpOnly ? 1 : 0),
    [age, genre, mpOnly]
  );

  const searching = !!query.trim();
  const showSkeletons = loading && visible.length === 0;

  return (
    <div className="ui-page">
      <div className="rb-cat__head">
        <h1 className="rb-cat__title">Каталог игр</h1>
        {total != null && !loading && (
          <span className="rb-cat__count ui-num">
            {searching ? "Найдено" : "Всего"}: {fmtNum(total)}
          </span>
        )}
      </div>

      <div className="rb-cat__toolbar">
        <Input
          className="rb-cat__search"
          type="search"
          placeholder="найти игру или автора…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          leadIcon={<CabIcon name="search" size={17} />}
          aria-label="Поиск игр"
        />
        {searching ? (
          <Button variant="ghost" onClick={clearQuery}>
            Сбросить
          </Button>
        ) : (
          <Button
            variant={activeCount ? "subtle" : "ghost"}
            onClick={() => setFiltersOpen((v) => !v)}
            icon={<CabIcon name="filter" size={16} />}
            aria-expanded={filtersOpen}
          >
            Фильтры{activeCount ? ` · ${activeCount}` : ""}
          </Button>
        )}
      </div>

      {/* При поиске вкладки и фильтры прячем: они относятся к каталогу,
          а поиск идёт по всей базе — иначе выглядит, будто фильтр не
          сработал. */}
      {!searching && (
        <>
          <Tabs items={SORTS} value={sort} onChange={onSort} className="rb-cat__tabs" />

          {filtersOpen && (
            <div className="rb-cat__filters">
              <div className="rb-cat__group">
                <span className="rb-cat__label">Возраст</span>
                <div className="rb-cat__chips">
                  <button
                    type="button"
                    className={`rb-cat__chip ${age === 18 ? "is-active" : ""}`}
                    onClick={() => onAge(18)}
                  >
                    Любой
                  </button>
                  {AGE_RATINGS.filter((a) => a < 18).map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={`rb-cat__chip ${age === a ? "is-active" : ""}`}
                      onClick={() => onAge(a)}
                    >
                      {a}+
                    </button>
                  ))}
                </div>
              </div>

              <div className="rb-cat__group">
                <span className="rb-cat__label">Жанр</span>
                <div className="rb-cat__chips">
                  <button
                    type="button"
                    className={`rb-cat__chip ${!genre ? "is-active" : ""}`}
                    onClick={() => onGenre("")}
                  >
                    Все
                  </button>
                  {genreOptions().map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      className={`rb-cat__chip ${genre === g.value ? "is-active" : ""}`}
                      onClick={() => onGenre(g.value)}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rb-cat__group">
                <span className="rb-cat__label">Ещё</span>
                <div className="rb-cat__chips">
                  <button
                    type="button"
                    className={`rb-cat__chip ${mpOnly ? "is-active" : ""}`}
                    onClick={() => onMp(!mpOnly)}
                  >
                    <CabIcon name="users" size={13} /> Только с друзьями
                  </button>
                  {activeCount > 0 && (
                    <Button variant="link" size="sm" onClick={resetAll}>
                      Сбросить фильтры
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Свёрнутая панель прячет условия — показываем их строкой,
              чтобы сужение выборки не выглядело поломкой каталога. */}
          {!filtersOpen && activeCount > 0 && (
            <div className="rb-cat__active">
              {age !== 18 && <Pill tone="soft">до {age}+</Pill>}
              {genre && (
                <Pill tone="soft">
                  {genreOptions().find((g) => g.value === genre)?.label || genre}
                </Pill>
              )}
              {mpOnly && <Pill tone="soft">с друзьями</Pill>}
              <Button variant="link" size="sm" onClick={resetAll}>
                Сбросить
              </Button>
            </div>
          )}
        </>
      )}

      {error ? (
        <EmptyState
          icon={<CabIcon name="warning" size={40} />}
          title="Не получилось загрузить"
          desc="Проверь соединение и попробуй ещё раз."
          action={
            <Button variant="primary" onClick={() => fetchPage(1, false)}>
              Повторить
            </Button>
          }
        />
      ) : showSkeletons ? (
        <div className="rb-gcard-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <GameCardSkeleton key={i} />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="search" size={40} />}
          title="Пока ничего не найдено"
          desc={
            searching
              ? "Попробуй другое название или имя автора."
              : "Под эти условия игр нет. Попробуй ослабить фильтры."
          }
          action={
            searching ? (
              <Button variant="ghost" onClick={clearQuery}>
                Показать все игры
              </Button>
            ) : (
              activeCount > 0 && (
                <Button variant="primary" onClick={resetAll}>
                  Сбросить фильтры
                </Button>
              )
            )
          }
        />
      ) : (
        <>
          <div className="rb-gcard-grid">
            {visible.map((g) => (
              <GameCard key={g.id} game={g} onClick={(id) => navigate(`/app/game/${id}`)} />
            ))}
            {/* Догрузка — скелетонами в той же сетке: раскладка не прыгает,
                и сразу видно, что дальше ещё есть. */}
            {loading &&
              Array.from({ length: 4 }).map((_, i) => <GameCardSkeleton key={`s${i}`} />)}
          </div>
          <div ref={sentinelRef} className="rb-cat__sentinel" />
        </>
      )}
    </div>
  );
}
